import React, {
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, message, Spin } from 'antd';
import { connect } from 'react-redux';
import moment from 'moment';
import { debounce } from 'lodash';
import intl from 'react-intl-universal';

import IntlContext from '@/components/Intl/IntlContext';
import { getPrintInfoList, updateState } from '@/redux/actions/bill/print';
import BigNumber, { format2Decimal, parse2Number } from '@/utils/number';
import { exportPDF } from '@/utils/pdf';
import { TenantSignStorage } from '@/utils/intl';
import CustomDrawer, { IdrawerRef } from '../CustomDrawer';
import CustomForm, { IFieldItem, IMyFormRef } from '../CustomForm';
import DragFieldList, { DragFieldRef, DynamicFieldItem } from '../CustomForm/DragFieldList';
import { getPrintCss, getPrintHtml } from './dynamicSplicingHtml';
import styles from './index.module.scss';

export interface IbillPrintDrawerRef extends Omit<IdrawerRef, 'openDrawer'> {
  openDrawer: (value?: { resvNo: string }) => void;
}

interface IbillPrintDrawerProps {
  myRef?: any;
}

let iframe = null;

const BillPrintDrawer: React.FC<any> = ({
  memberLevelMap,
  userInfo,
  orderInfoResponse,
  consumeBills,
  paymentBills,
  accountTotal,
  hotelBasicInfoResp,
  myRef,
  updateStateFn,
  getPrintInfoListFn,
  printInfoListLoading,
}) => {
  const win: any = window;

  const [printLoading, setPrintLoading] = useState<boolean>(false);
  const [exportLoading, setExportLoading] = useState<boolean>(false);

  // 商户名称
  const merchantName = TenantSignStorage.getLocalConfig()?.marchantName || '--';

  const drawerRef = useRef<IbillPrintDrawerRef>();
  const baseRef = useRef<IMyFormRef>();
  const billTotalRef = useRef<IMyFormRef>();
  const otherRef = useRef<IMyFormRef>();
  const consumeDragFieldref = useRef<DragFieldRef>();
  const payDragFieldref = useRef<DragFieldRef>();
  const intlCtx = useContext(IntlContext);
  const { baseFormAttr, billTotalFormAttr, otherFormAttr } = useMemo(() => {
    const formAttrConfig: Record<string, IFieldItem[]> = {
      baseFormAttr: [
        {
          label: intl.get('cpm_bill_print_hotel'),
          key: 'storeName',
          rules: [
            { required: true, message: intl.get('cpm_bill_print_input_words_limit_50'), max: 50 },
          ],
          style: {
            maxWidth: 1000,
          },
        },
        {
          label: intl.get('bill_invoice_history_order_no'),
          key: 'resvNo',
          editable: false,
          rules: [{ required: true, message: intl.get('hooks_order_no_empty') }],
          style: {
            maxWidth: 1000,
          },
        },
        {
          label: intl.get('cpm_bill_print_time'),
          key: 'printTime',
          type: 'datePicker',
          rules: [{ required: true, message: intl.get('cpm_bill_print_select_print_time') }],
          style: {
            maxWidth: 1000,
          },
        },
        {
          label: intl.get('app_name'),
          key: 'masterName',
          rules: [
            { required: true, message: intl.get('cpm_bill_print_input_words_limit_50'), max: 50 },
          ],
          style: {
            maxWidth: 1000,
          },
        },
        {
          label: intl.get('app_reservation_cert_number'),
          key: 'masterCertNo',
          rules: [
            { required: false, message: intl.get('cpm_bill_print_input_words_limit_50'), max: 50 },
          ],
          style: {
            maxWidth: 1000,
          },
        },
        {
          label: intl.get('app_reservation_member_level'),
          key: 'memberLevelName',
          rules: [
            { required: true, message: intl.get('cpm_bill_print_input_words_limit_50'), max: 50 },
          ],
          style: {
            maxWidth: 1000,
          },
        },
        {
          label: intl.get('app_reservation_remain_balance'),
          key: 'balance',
          editable: false,
          componentProps: { prefix: intlCtx.TenantBaseConfig.CurrencySymbol },
          style: {
            maxWidth: 1000,
          },
        },
        {
          label: intl.get('cpm_bill_print_arrival_time'),
          key: 'expectedArrivalTime',
          type: 'datePicker',
          rules: [
            { required: true, message: intl.get('cpm_bill_print_please_select_arrival_time') },
          ],
          style: {
            maxWidth: 1000,
          },
        },
        {
          label: intl.get('channel_leave_time'),
          key: 'expectedDepartureTime',
          type: 'datePicker',
          rules: [{ required: true, message: intl.get('cpm_bill_print_select_arrive_time') }],
          style: {
            maxWidth: 1000,
          },
        },
      ],

      billTotalFormAttr: [
        {
          label: intl.get('cpm_bill_print_total_consumption'),
          key: 'consumeTotal',
          editable: false,
          componentProps: { prefix: intlCtx.TenantBaseConfig.CurrencySymbol },
          style: {
            maxWidth: 1000,
          },
        },
        {
          label: intl.get('cpm_bill_print_discount_total'),
          key: 'discountToTal',
          editable: false,
          componentProps: { prefix: intlCtx.TenantBaseConfig.CurrencySymbol },
          style: {
            maxWidth: 1000,
          },
        },
        {
          label: intl.get('cpm_bill_print_total_consumption_after_discounts'),
          key: 'consumeAfterDiscountTotal',
          editable: false,
          componentProps: { prefix: intlCtx.TenantBaseConfig.CurrencySymbol },
          style: {
            maxWidth: 1000,
          },
        },
        {
          label: intl.get('bill_pay_total'),
          key: 'paymentTotal',
          editable: false,
          componentProps: { prefix: intlCtx.TenantBaseConfig.CurrencySymbol },
          style: {
            maxWidth: 1000,
          },
        },
        {
          label: intl.get('cpm_bill_print_total_refund'),
          key: 'refundTotal',
          editable: false,
          componentProps: { prefix: intlCtx.TenantBaseConfig.CurrencySymbol },
          style: {
            maxWidth: 1000,
          },
        },
      ],

      otherFormAttr: [
        {
          label: intl.get('cpm_bill_print_hotel_phone'),
          key: 'storePhone',
          rules: [
            { required: true, message: intl.get('cpm_bill_print_input_words_limit_50'), max: 50 },
          ],
          style: {
            maxWidth: 1000,
          },
        },
        {
          label: intl.get('cpm_bill_print_hotel_address'),
          key: 'storeAddress',
          rules: [
            { required: true, message: intl.get('cpm_bill_print_input_words_limit_50'), max: 50 },
          ],
          style: {
            maxWidth: 1000,
          },
        },
        {
          label: intl.get('cpm_bill_print_operator'),
          key: 'operater',
          rules: [
            { required: true, message: intl.get('cpm_bill_print_input_words_limit_50'), max: 50 },
          ],
          style: {
            maxWidth: 1000,
          },
        },
      ],
    };
    return formAttrConfig;
  }, [intlCtx.TenantBaseConfig.CurrencySymbol]);

  const handleOpenDrawer = ({ resvNo }) => {
    getPrintInfoListFn({ resvNo }, memberLevelMap);
    drawerRef.current.openDrawer();
  };

  const handleCancel = () => {
    drawerRef.current.closeDrawer();
    setPrintLoading(false);
    setExportLoading(false);
  };

  const electronPrint = async (cssStr, htmlStr) => {
    win?.electron?.ipcRenderer?.sendPrintData({
      cssStr,
      htmlStr,
    });
  };

  // 生成打印参数
  const getPrintParams = () => {
    return new Promise((resolve, reject) => {
      Promise.all([
        baseRef.current.validateFields(),
        consumeDragFieldref.current.validateFields(),
        payDragFieldref.current.validateFields(),
        billTotalRef.current.validateFields(),
        otherRef.current.validateFields(),
      ])
        .then((resArray) => {
          const [
            _orderInfoResponse,
            _consumeBills,
            _paymentBills,
            _accountTotal,
            _hotelBasicInfoResp,
          ] = resArray;
          const params = {
            orderInfoResponse: { ...orderInfoResponse, ..._orderInfoResponse },
            consumeBills: _consumeBills.fieldList,
            paymentBills: _paymentBills.fieldList,
            accountTotal: _accountTotal,
            hotelBasicInfoResp: { ...hotelBasicInfoResp, ..._hotelBasicInfoResp },
          };
          resolve(params);
        })
        .catch(() => {
          message.warning(intl.get('cpm_bill_print_check_has_info_not_filled'));
          reject();
        });
    });
  };

  const handlePrint = async () => {
    setPrintLoading(true);
    try {
      const params = await getPrintParams();
      const cssStr = getPrintCss();
      const htmlStr = getPrintHtml(params);
      if (win.electron) {
        electronPrint(cssStr, htmlStr);
      } else {
        if (iframe) {
          try {
            document.body.removeChild(iframe);
          } catch (error) {
            //
          }
        }
        iframe = document.createElement('iframe');
        // iframe.src = '/print';
        document.body.appendChild(iframe);
        iframe.contentDocument.head.innerHTML = cssStr;
        iframe.contentDocument.body.innerHTML = htmlStr;
        iframe.contentWindow.print();
        setPrintLoading(false);
        // iframe.contentWindow.addEventListener('afterprint', (event) => {
        //   console.log('After print');
        // });
        // iframe.contentWindow.addEventListener('beforeprint', (event) => {
        //   console.log('beforeprint');
        // });
      }
    } catch (err) {
      setPrintLoading(false);
    }
  };

  // 生成PDF内容
  const generateDoc = (wrapperId: string, docStr: string) => {
    return new Promise((resolve) => {
      const wrapperEl = document.getElementById(wrapperId);
      // remove old node
      if (wrapperEl !== null) document.body.removeChild(wrapperEl);

      // create new node
      const newEl = document.createElement('div');
      newEl.id = wrapperId;
      newEl.innerHTML = docStr;

      // append to body
      document.body.appendChild(newEl);

      resolve(true);
    });
  };

  // 导出PDF
  const handlePdfExport = async (name: string) => {
    const pdfName = name || intl.get('cpm_bill_print_guest_detail_bill_form');
    const wrapperId = 'printBill';

    setExportLoading(true);

    try {
      // generate doc before download
      const params = await getPrintParams();
      const html = getPrintHtml(params);
      const docStr = ''.concat(getPrintCss(), '\n', html);
      await generateDoc(wrapperId, docStr);

      const dom = document.getElementById(wrapperId);
      dom.style.width = '1000px';
      dom.style.padding = '20px';
      if (dom === null) {
        message.error(intl.get('cpm_bill_print_doc_genarete_fail_can_not_download'));
        return;
      }

      await exportPDF(pdfName, dom);
      setExportLoading(false);
    } catch (err) {
      setExportLoading(false);
    }
  };

  // 抽屉关闭后回调
  const visibleChange = (visible) => {
    if (!visible) {
      const el = document.getElementById('printBill');
      // remove node
      if (el !== null) document.body.removeChild(el);

      if (iframe) {
        try {
          document.body.removeChild(iframe);
        } catch (error) {
          //
        }
      }
    }
  };

  const handleAddConsumeItem = () => {
    consumeDragFieldref.current?.addFn();
  };

  const handleAddPayItem = () => {
    payDragFieldref.current?.addFn();
  };

  // 计算消费账目合计
  const calcConsumeDebounce = debounce((changedValues, allValues) => {
    let totalAmount = 0;
    let totalDiscountAmount = 0;
    let consumeAfterDiscountTotal = 0;
    const { fieldList } = changedValues || {};
    if (fieldList && Array.isArray(fieldList) && fieldList.length) {
      const isExistAmount = allValues?.fieldList?.some((item) => 'amount' in item);
      const isExistDiscountAmount = allValues?.fieldList?.some((item) => 'discountAmount' in item);
      if (isExistAmount) {
        totalAmount = allValues?.fieldList?.reduce(
          (total, item) => total + (parse2Number(item.amount) || 0),
          0,
        );
      }
      if (isExistDiscountAmount) {
        totalDiscountAmount = allValues?.fieldList?.reduce(
          (total, item) => total + (parse2Number(item.discountAmount) || 0),
          0,
        );
      }
      consumeAfterDiscountTotal = new BigNumber(totalAmount - totalDiscountAmount).toNumber();
    }
    updateStateFn({
      accountTotal: {
        ...accountTotal,
        consumeTotal: format2Decimal(totalAmount),
        discountToTal: format2Decimal(totalDiscountAmount),
        consumeAfterDiscountTotal: format2Decimal(consumeAfterDiscountTotal),
      },
    });
  }, 200);
  const handleConsumeValuesChange = (changedValues, allValues) => {
    calcConsumeDebounce(changedValues, allValues);
  };

  // 计算退款账目合计
  const calcPayDebounce = debounce((changedValues, allValues) => {
    let totalAmount = 0;
    let totalRefundAmount = 0;
    const { fieldList } = changedValues || {};
    if (fieldList && Array.isArray(fieldList) && fieldList.length) {
      if (allValues?.fieldList?.some((item) => 'amount' in item)) {
        totalAmount = allValues?.fieldList?.reduce(
          (total, item) => total + (parse2Number(item.amount) || 0),
          0,
        );
      }
      if (allValues?.fieldList?.some((item) => 'refundAmount' in item)) {
        totalRefundAmount = allValues?.fieldList?.reduce(
          (total, item) => total + (parse2Number(item.refundAmount) || 0),
          0,
        );
      }
    }
    updateStateFn({
      accountTotal: {
        ...accountTotal,
        paymentTotal: format2Decimal(totalAmount),
        refundTotal: format2Decimal(totalRefundAmount),
      },
    });
  }, 200);
  const handlePayValuesChange = (changedValues, allValues) => {
    calcPayDebounce(changedValues, allValues);
  };

  // 格式化自动补两位小数
  const formatConsumeMoney2Decimal = async (value, index, key) => {
    const { fieldList } = (await consumeDragFieldref.current.getFieldsValue()) || {};
    const consumeList = [...fieldList];
    if (consumeList[index] && consumeList[index][key]) {
      consumeList[index][key] = value ? format2Decimal(new BigNumber(value)) : '';
    }
    updateStateFn({
      consumeBills: consumeList,
    });
  };
  const formatPayMoney2Decimal = async (value, index, key) => {
    const { fieldList } = (await payDragFieldref.current.getFieldsValue()) || {};
    const payList = [...fieldList];
    if (payList[index] && payList[index][key]) {
      payList[index][key] = value ? format2Decimal(new BigNumber(value)) : '';
    }
    updateStateFn({
      paymentBills: payList,
    });
  };
  const onBlur = useCallback((value, index, key, type?: string) => {
    if ((key === 'amount' && type === 'consume') || key === 'discountAmount') {
      formatConsumeMoney2Decimal(value, index, key);
    }
    if ((key === 'amount' && type === 'refund') || key === 'refundAmount') {
      formatPayMoney2Decimal(value, index, key);
    }
  }, []);

  useEffect(() => {
    if (!consumeBills || (Array.isArray(consumeBills) && !consumeBills.length)) {
      consumeDragFieldref.current?.addFn();
    }
    if (!paymentBills || (Array.isArray(paymentBills) && !paymentBills.length)) {
      payDragFieldref.current?.addFn();
    }
  }, [consumeBills, paymentBills]);

  useEffect(() => {
    message.config({
      maxCount: 1,
      // rtl: true,
    });
    try {
      win?.electron?.ipcRenderer.on('print-result', ({ success }) => {
        setPrintLoading(false);
        if (success) {
          message.success(intl.get('bill_pay_order_list_modal_print_success'));
        } else {
          message.error(intl.get('bill_pay_order_list_modal_print_fail'));
          // console.log('electron---error', failureReason);
        }
      });
    } catch (error) {
      setPrintLoading(false);
    }
  }, []);

  useImperativeHandle(
    myRef,
    () => ({
      ...drawerRef.current,
      openDrawer: handleOpenDrawer,
    }),
    [],
  );

  const baseDetail = useMemo(() => {
    return {
      ...orderInfoResponse,
      storeName: hotelBasicInfoResp?.storeName?.includes('网鱼电竞酒店')
        ? hotelBasicInfoResp?.storeName || ''
        : `${merchantName}(${hotelBasicInfoResp?.storeName || ''})`,
      printTime: moment(),
    };
  }, [orderInfoResponse, hotelBasicInfoResp]);
  const otherDetail = useMemo(() => {
    return {
      ...hotelBasicInfoResp,
      operater: userInfo?.name,
    };
  }, [hotelBasicInfoResp, userInfo]);

  const [consumeFormAttr, payFormAttr]: [
    Array<DynamicFieldItem>,
    Array<DynamicFieldItem>,
  ] = useMemo(() => {
    const consumeAttr: Array<DynamicFieldItem> = [
      {
        label: intl.get('bill_invoice_modal_time'),
        span: 5,
        key: 'createTime',
        type: 'dateTime',
        rules: [{ required: true, message: intl.get('expenses_please_select_date') }],
      },
      {
        label: intl.get('bill_invoice_modal_room_no'),
        span: 2,
        key: 'roomNo',
        rules: [
          {
            required: true,
            message: intl.get('cpm_bill_print_words_limit_50'),
            max: 50,
          },
        ],
      },
      {
        label: intl.get('bill_refound_res_bill_subject'),
        span: 3,
        key: 'businessExplain',
        type: 'textArea',
        rules: [
          {
            required: true,
            message: intl.get('app_search_form_validator_charactor', { count: 10000 }),
            max: 10000,
          },
        ],
      },
      {
        label: intl.get('bill_detail'),
        span: 5,
        key: 'detail',
        type: 'textArea',
        rules: [
          {
            required: true,
            message: intl.get('app_search_form_validator_charactor', { count: 10000 }),
            max: 10000,
          },
        ],
      },
      {
        label: intl.get('bill_credit_amount'),
        span: 3,
        key: 'amount',
        type: 'inputMoney',
        rules: [{ required: true, message: intl.get('cpm_bill_print_integer_limit') }],
        fieldProps: {
          onBlur: (value, index) => onBlur(value, index, 'amount', 'consume'),
        },
      },
      {
        label: intl.get('bill_config_preferential'),
        span: 3,
        key: 'discountAmount',
        type: 'inputMoney',
        rules: [{ required: true, message: intl.get('cpm_bill_print_integer_limit') }],
        fieldProps: {
          onBlur: (value, index) => onBlur(value, index, 'discountAmount'),
        },
      },
    ];
    const payAttr = [...consumeAttr] as any;
    payAttr.splice(
      payAttr.length - 2,
      2,
      {
        label: intl.get('bill_credit_amount'),
        span: 3,
        key: 'amount',
        type: 'inputMoney',
        rules: [{ required: true, message: intl.get('cpm_bill_print_integer_limit') }],
        fieldProps: {
          onBlur: (value, index) => onBlur(value, index, 'amount', 'refund'),
        },
      },
      {
        label: intl.get('bill_config_refound'),
        span: 3,
        key: 'refundAmount',
        type: 'inputMoney',
        rules: [{ required: true, message: intl.get('cpm_bill_print_integer_limit') }],
        fieldProps: {
          onBlur: (value, index) => onBlur(value, index, 'refundAmount'),
        },
      },
    );
    return [consumeAttr, payAttr];
  }, []);

  return (
    <>
      <CustomDrawer
        width="90%"
        title={intl.get('cpm_bill_print_bill_print_adjust')}
        footer={
          <>
            <Button
              type="primary"
              loading={printInfoListLoading || printLoading}
              onClick={handlePrint}
            >
              {intl.get('app_print')}
            </Button>
            <Button
              loading={printInfoListLoading || exportLoading}
              onClick={() => handlePdfExport('')}
              style={{ color: '#3f79ff', borderColor: '#3f79ff' }}
            >
              {intl.get('cpm_bill_print_export_pdf')}
            </Button>
            <Button onClick={handleCancel}>{intl.get('app_confirm_cancel_btn')}</Button>
          </>
        }
        onClose={handleCancel}
        afterOpenChange={(visible) => visibleChange(visible)}
        ref={drawerRef}
      >
        <Spin spinning={printInfoListLoading || printLoading || exportLoading}>
          <div className={styles.container}>
            <div className="space-between" style={{ margin: '32px 0' }}>
              <div className="groupLabel">{intl.get('channel_basic_info')}</div>
            </div>
            <CustomForm
              ref={baseRef}
              formAttr={baseFormAttr}
              labelCol={{ span: 6 }}
              labelAlign="left"
              wrapperCol={{ span: 9 }}
              value={baseDetail}
            />
            <div className="space-between" style={{ margin: '32px 0', alignItems: 'flex-start' }}>
              <div className="groupLabel">{intl.get('cpm_bill_print_custom_bill')}</div>
              <div className={styles.addBtn} onClick={handleAddConsumeItem}>
                {intl.get('cpm_bill_print_add_custom_bill')}
              </div>
            </div>
            <DragFieldList
              ref={consumeDragFieldref}
              dynamicFormAttr={consumeFormAttr}
              dynamicDataSource={consumeBills}
              onValuesChange={handleConsumeValuesChange}
            />
            <div className="space-between" style={{ margin: '32px 0', alignItems: 'flex-start' }}>
              <div className="groupLabel">{intl.get('cpm_bill_print_pay_bill')}</div>
              <div className={styles.addBtn} onClick={handleAddPayItem}>
                {intl.get('cpm_bill_print_add_pay_bill')}
              </div>
            </div>
            <DragFieldList
              ref={payDragFieldref}
              dynamicFormAttr={payFormAttr}
              dynamicDataSource={paymentBills}
              onValuesChange={handlePayValuesChange}
            />
            <div className="space-between" style={{ margin: '32px 0' }}>
              <div className="groupLabel">{intl.get('bill_part_settle_account_total')}</div>
            </div>
            <CustomForm
              ref={billTotalRef}
              formAttr={billTotalFormAttr}
              labelCol={{ span: 6 }}
              labelAlign="left"
              wrapperCol={{ span: 9 }}
              value={accountTotal}
            />
            <div className="space-between" style={{ margin: '32px 0' }}>
              <div className="groupLabel">{intl.get('device_other_info')}</div>
            </div>
            <CustomForm
              ref={otherRef}
              formAttr={otherFormAttr}
              labelCol={{ span: 6 }}
              labelAlign="left"
              wrapperCol={{ span: 9 }}
              value={otherDetail}
            />
          </div>
        </Spin>
      </CustomDrawer>
    </>
  );
};

const mapStateToProps = (state) => {
  return { ...state.guest, ...state.user, ...state.print };
};

const ConnectComponent: React.FC<IbillPrintDrawerProps> = connect(mapStateToProps, {
  updateStateFn: updateState,
  getPrintInfoListFn: getPrintInfoList,
})(React.memo(BillPrintDrawer));

export default React.forwardRef<IbillPrintDrawerRef, IbillPrintDrawerProps>((props, ref) => (
  <ConnectComponent {...props} myRef={ref} />
));
